import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

const DEVICE_SETTINGS_CHANGE_DEDUPE_WINDOW_MS = 5_000;
const DEVICE_SETTINGS_CHANGE_DEDUPE_MAX_ENTRIES = 500;

type DeviceSettingsChangeEntry = {
  canonicalValue: string;
  timestamp: number;
};

const recentDeviceSettingsByKey = new Map<string, DeviceSettingsChangeEntry>();

const withNonCohortAliases = (properties: EventProperties): EventProperties => ({
  ...properties,
  ...(properties.device_id ? { deviceId: properties.device_id } : {})
});

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const toCanonicalDeviceSettingsValue = (value: unknown): unknown => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (typeof value === 'undefined') {
    return '__undefined__';
  }

  if (typeof value === 'bigint') {
    return `__bigint__:${value.toString()}`;
  }

  if (typeof value === 'symbol') {
    return `__symbol__:${String(value)}`;
  }

  if (typeof value === 'function') {
    return `__function__:${value.name || 'anonymous'}`;
  }

  if (value instanceof Date) {
    return `__date__:${value.toISOString()}`;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => toCanonicalDeviceSettingsValue(entry));
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entryValue]) => [key, toCanonicalDeviceSettingsValue(entryValue)])
    );
  }

  return String(value);
};

const canonicalizeDeviceSettingsValue = (value: unknown): string =>
  JSON.stringify(toCanonicalDeviceSettingsValue(value));

const evictExpiredDeviceSettingsChanges = (now: number): void => {
  if (recentDeviceSettingsByKey.size < DEVICE_SETTINGS_CHANGE_DEDUPE_MAX_ENTRIES) {
    return;
  }

  for (const [key, entry] of recentDeviceSettingsByKey) {
    if (now - entry.timestamp > DEVICE_SETTINGS_CHANGE_DEDUPE_WINDOW_MS) {
      recentDeviceSettingsByKey.delete(key);
    }
  }
};

const shouldEmitDeviceSettingsChange = (dedupeKey: string, canonicalValue: string): boolean => {
  const now = Date.now();
  const last = recentDeviceSettingsByKey.get(dedupeKey);

  if (
    last !== undefined &&
    now - last.timestamp < DEVICE_SETTINGS_CHANGE_DEDUPE_WINDOW_MS &&
    last.canonicalValue === canonicalValue
  ) {
    return false;
  }

  evictExpiredDeviceSettingsChanges(now);
  recentDeviceSettingsByKey.set(dedupeKey, { canonicalValue, timestamp: now });

  return true;
};

/** @internal */
export const resetDeviceSettingsChangeDedupeState = (): void => {
  recentDeviceSettingsByKey.clear();
};

/** @internal */
export const getDeviceSettingsChangeDedupeSize = (): number => recentDeviceSettingsByKey.size;

export interface DeviceSettingsChangeProperties extends EventProperties {
  command_key: string;
  command: string;
  value: unknown;
  device_id?: string;
  brand_name?: string;
  model_name?: string;
  device_brand_id?: string;
  device_model_id?: string;
  include_legacy_setting_value?: boolean;
}

export const createSettingsLifecycleTracker = (track: AnalyticsTrackFn) => ({
  trackAdvancedSettingsOpened: (properties: EventProperties = {}): void => {
    track(
      EVENT_NAMES.ADVANCED_SETTINGS_OPENED,
      EVENT_CATEGORIES.ADVANCED_SETTINGS,
      withNonCohortAliases(properties)
    );
  },

  // W5: advanced_settings_closed completes the open/close funnel pair.
  // advanced_settings_section_opened was removed (ENG-3960 §D11 cleanup).
  trackAdvancedSettingsClosed: (properties: EventProperties = {}): void => {
    track(
      EVENT_NAMES.ADVANCED_SETTINGS_CLOSED,
      EVENT_CATEGORIES.ADVANCED_SETTINGS,
      withNonCohortAliases(properties)
    );
  },

  trackAdvancedSettingsExported: (properties: EventProperties = {}): void => {
    track(
      EVENT_NAMES.ADVANCED_SETTINGS_EXPORTED,
      EVENT_CATEGORIES.ADVANCED_SETTINGS,
      withNonCohortAliases(properties)
    );
  },

  trackDeviceSettingsChange: ({
    include_legacy_setting_value = true,
    ...properties
  }: DeviceSettingsChangeProperties): void => {
    const dedupeKey = `${properties.device_id ?? ''}:${properties.command_key}`;
    const canonicalValue = canonicalizeDeviceSettingsValue(properties.value);

    if (!shouldEmitDeviceSettingsChange(dedupeKey, canonicalValue)) {
      return;
    }

    track(EVENT_NAMES.DEVICE_SETTINGS_CHANGE, EVENT_CATEGORIES.DEVICE, {
      ...withNonCohortAliases(properties),
      ...(include_legacy_setting_value ? { settingValue: properties.value } : {})
    });
  }
});
